import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  BarChart2,
  BookOpen,
  Brain,
  Calendar,
  CheckCircle,
  FileText,
  Menu,
  Search,
  ShoppingCart,
  Smartphone,
  Sprout,
  X,
} from 'lucide-react';
import { BOOK_BUNDLES } from '../data/bookBundles';
import { STATIC_BLOG_POSTS } from '../data/staticBlogPosts';
import { useAuth } from '../context/AuthContext';

const API_URL = import.meta.env.VITE_API_URL;

const NAV_LINKS = [
  { label: 'Home', href: '#home' },
  { label: 'Free Plan', href: '#free-plan' },
  { label: 'Books', href: '#books' },
  { label: 'Articles', href: '#articles' },
  { label: 'Apps', href: '#apps' },
  { label: 'About', href: '#about' },
];

const APP_FEATURES = [
  {
    icon: BarChart2,
    title: 'Track nutrition',
    text: 'Log meals, watch macros, and understand your daily patterns without overcomplicating food.',
  },
  {
    icon: Brain,
    title: 'Ask the AI coach',
    text: 'Get practical food, grocery, and meal questions answered around your goals and remaining macros.',
  },
  {
    icon: ShoppingCart,
    title: 'Plan groceries',
    text: 'Turn nutrition goals into a usable shopping list for the week.',
  },
];

const FEATURED_PATHS = [
  'the-gut-reset',
  'the-longevity-blueprint',
  'macros-made-easy-for-women',
  'glp1-companion-for-women-over-50',
];

const scrollTo = (id) => {
  document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
};

const formatDate = (d) => {
  if (!d) return '';
  return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const decodeEntities = (str) => {
  if (!str) return '';
  return str
    .replace(/&#(\d+);/g, (_, code) => String.fromCharCode(parseInt(code, 10)))
    .replace(/&amp;/g, '&')
    .replace(/&quot;/g, '"')
    .replace(/&apos;/g, "'")
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>');
};

export default function LandingPage() {
  const { isAuthenticated } = useAuth();
  const [menuOpen, setMenuOpen] = useState(false);
  const [recentPosts, setRecentPosts] = useState([]);

  const featuredBooks = useMemo(() => (
    FEATURED_PATHS
      .map((slug) => BOOK_BUNDLES.find((book) => book.slug === slug))
      .filter(Boolean)
  ), []);

  useEffect(() => {
    document.title = 'Integrative Health Journal | Books, Articles, and Nutrition Apps';
  }, []);

  useEffect(() => {
    fetch(`${API_URL}/blog/recent?limit=3`)
      .then((r) => r.json())
      .then((posts) => {
        const apiPosts = Array.isArray(posts) ? posts : [];
        const merged = [
          ...STATIC_BLOG_POSTS,
          ...apiPosts.filter((post) => !STATIC_BLOG_POSTS.some((item) => item.slug === post.slug)),
        ];
        setRecentPosts(merged.slice(0, 3));
      })
      .catch(() => setRecentPosts(STATIC_BLOG_POSTS.slice(0, 3)));
  }, []);

  const appCta = isAuthenticated ? '/dashboard' : '/login?tab=signup';
  const appCtaLabel = isAuthenticated ? 'Open NutriTrack' : 'Start free';

  return (
    <div className="min-h-screen bg-[#f8f3ea] text-[#1f2a22] overflow-x-hidden">
      <nav className="fixed inset-x-0 top-0 z-50 border-b border-[#ded2bf] bg-[#fbf7ef]/92 backdrop-blur-md">
        <div className="mx-auto flex h-[72px] max-w-7xl items-center justify-between px-4 sm:px-6">
          <Link to="/" className="flex min-w-0 items-center gap-3 py-2">
            <img src="/images/ihj-facebook-logo.jpg" alt="Integrative Health Journal" className="h-12 w-12 shrink-0 rounded-full object-cover shadow-sm ring-1 ring-[#c9b99d]" />
            <span className="min-w-0">
              <span className="block truncate font-serif text-xl font-semibold tracking-normal text-[#1f2a22]">
                Integrative Health Journal
              </span>
              <span className="hidden text-xs uppercase tracking-[0.24em] text-[#7b806f] sm:block">
                Guides, trackers, tools
              </span>
            </span>
          </Link>

          <div className="hidden items-center gap-7 md:flex">
            {NAV_LINKS.map((link) => (
              <button
                key={link.label}
                type="button"
                onClick={() => scrollTo(link.href.slice(1))}
                className="text-sm font-medium text-[#4d574c] transition-colors hover:text-[#1f2a22]"
              >
                {link.label}
              </button>
            ))}
          </div>

          <div className="hidden items-center gap-3 md:flex">
            <Link to="/blog" className="rounded-full border border-[#c9b99d] px-4 py-2 text-sm font-semibold text-[#334033] hover:bg-[#efe4d2]">
              Articles
            </Link>
            <Link to={appCta} className="rounded-full bg-[#536343] px-5 py-2 text-sm font-bold text-white shadow-sm hover:bg-[#435236]">
              {appCtaLabel}
            </Link>
          </div>

          <button
            type="button"
            className="rounded-full border border-[#d4c7b3] p-2 text-[#344034] md:hidden"
            onClick={() => setMenuOpen((open) => !open)}
            aria-label="Open navigation menu"
          >
            {menuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>

        {menuOpen && (
          <div className="border-t border-[#ded2bf] bg-[#fbf7ef] px-4 py-4 md:hidden">
            <div className="space-y-2">
              {NAV_LINKS.map((link) => (
                <button
                  key={link.label}
                  type="button"
                  onClick={() => { scrollTo(link.href.slice(1)); setMenuOpen(false); }}
                  className="block w-full rounded-lg px-2 py-2 text-left text-sm font-semibold text-[#394538] hover:bg-[#efe4d2]"
                >
                  {link.label}
                </button>
              ))}
                <Link to="/books" onClick={() => setMenuOpen(false)} className="block rounded-lg px-2 py-2 text-sm font-semibold text-[#394538] hover:bg-[#efe4d2]">
                Shop Books
              </Link>
              <Link to="/free-macro-starter-plan" onClick={() => setMenuOpen(false)} className="block rounded-lg px-2 py-2 text-sm font-semibold text-[#394538] hover:bg-[#efe4d2]">
                Free Macro Starter Plan
              </Link>
              <Link to={appCta} onClick={() => setMenuOpen(false)} className="block rounded-lg bg-[#536343] px-4 py-3 text-center text-sm font-bold text-white">
                {appCtaLabel}
              </Link>
            </div>
          </div>
        )}
      </nav>

      <main id="home">
        <section className="relative min-h-[92vh] px-4 pt-28 sm:px-6">
          <div className="mx-auto grid max-w-7xl gap-10 pb-16 lg:grid-cols-[1.03fr_0.97fr] lg:items-center">
            <div className="pt-8 lg:pt-12">
              <div className="mb-7 inline-flex items-center gap-2 border-y border-[#c9b99d] px-4 py-2 text-xs font-semibold uppercase tracking-[0.24em] text-[#6f785f]">
                <Sprout className="h-4 w-4" />
                Practical wellness guides, trackers, and nutrition tools
              </div>

              <h1 className="font-serif text-5xl font-semibold leading-[0.98] tracking-normal text-[#182018] sm:text-6xl lg:text-7xl">
                Integrative Health Journal
              </h1>
              <p className="mt-6 max-w-2xl text-lg leading-8 text-[#596154]">
                A calm, practical home for wellness books, nutrition articles, and the NutriTrack app. Built for women creating healthier routines with less guesswork and more follow-through.
              </p>

              <div className="mt-8 flex flex-col gap-3 sm:flex-row">
                <Link to="/free-macro-starter-plan" className="inline-flex items-center justify-center gap-2 rounded-full bg-[#536343] px-6 py-3 text-sm font-bold text-white shadow-md hover:bg-[#435236]">
                  Get free macro plan <ArrowRight className="h-4 w-4" />
                </Link>
                <Link to="/books" className="inline-flex items-center justify-center gap-2 rounded-full border border-[#b9a98f] bg-[#fbf7ef] px-6 py-3 text-sm font-bold text-[#334033] hover:bg-[#efe4d2]">
                  Shop book bundles <ArrowRight className="h-4 w-4" />
                </Link>
                <Link to="/blog" className="inline-flex items-center justify-center gap-2 rounded-full border border-[#b9a98f] bg-white/60 px-6 py-3 text-sm font-bold text-[#334033] hover:bg-[#efe4d2]">
                  Read articles <FileText className="h-4 w-4" />
                </Link>
              </div>
            </div>

            <div className="relative">
              <div className="overflow-hidden rounded-[2rem] border border-[#d8cbb8] bg-[#efe4d2] shadow-2xl shadow-[#9b8d73]/20">
                <div className="relative min-h-[530px] bg-[radial-gradient(circle_at_15%_20%,rgba(255,255,255,0.85),transparent_30%),linear-gradient(135deg,#f9f5ec_0%,#e9dfcc_48%,#d9d0b8_100%)] p-8">
                  <div className="absolute left-8 top-8 h-20 w-20 rounded-full border border-[#d5c4a7] bg-[#fbf7ef]/80" />
                  <div className="absolute right-8 top-8 h-28 w-28 rounded-full border border-[#d5c4a7] bg-[#c8d1b8]/45" />
                  <div className="absolute inset-x-8 top-16 flex justify-center">
                    <div className="text-center">
                      <img src="/images/ihj-facebook-logo.jpg" alt="" className="mx-auto h-24 w-24 rounded-full object-cover shadow-sm ring-1 ring-[#c9b99d]" />
                      <div className="mt-4 flex items-center justify-center gap-3 text-[#6f785f]">
                        <span className="h-px w-20 bg-[#b9a98f]" />
                        <Sprout className="h-4 w-4" />
                        <span className="h-px w-20 bg-[#b9a98f]" />
                      </div>
                    </div>
                  </div>

                  <div className="absolute bottom-8 left-8 right-8 grid gap-4 sm:grid-cols-[1fr_0.85fr]">
                    <div className="rounded-2xl border border-[#d7c8ae] bg-[#fbf7ef]/90 p-5 shadow-lg shadow-[#9b8d73]/10">
                      <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#7b806f]">Journal system</p>
                      <h2 className="mt-2 font-serif text-3xl font-semibold text-[#1f2a22]">Books + trackers + app</h2>
                      <p className="mt-3 text-sm leading-6 text-[#626b5d]">
                        Digital guides, printable PDFs, and NutriTrack access for practical daily follow-through.
                      </p>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      {featuredBooks.slice(0, 4).map((book) => (
                        <Link key={book.slug} to={book.slug === 'macros-made-easy-for-women' ? '/books/macros-made-easy-for-women' : `/books#${book.slug}`} className="block">
                          <img
                            src={book.image}
                            alt={`${book.title} cover`}
                            className="aspect-[2/3] w-full rounded-lg object-cover shadow-lg ring-1 ring-[#806f52]/20"
                            loading="lazy"
                          />
                        </Link>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="border-y border-[#d8cbb8] bg-[#ede3d3] px-4 py-5 sm:px-6">
          <div className="mx-auto grid max-w-7xl gap-4 text-center sm:grid-cols-3">
            {[
              ['Books', 'Digital guides with printable PDFs'],
              ['Articles', 'Evidence-informed wellness education'],
              ['Apps', 'NutriTrack tools for daily use'],
            ].map(([title, text]) => (
              <div key={title} className="px-4 py-3">
                <div className="font-serif text-2xl font-semibold text-[#1f2a22]">{title}</div>
                <p className="mt-1 text-sm text-[#646b5e]">{text}</p>
              </div>
            ))}
          </div>
        </section>

        <section id="about" className="px-4 py-20 sm:px-6">
          <div className="mx-auto grid max-w-7xl gap-10 lg:grid-cols-[0.85fr_1.15fr] lg:items-start">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.28em] text-[#7b806f]">About IHJ</p>
              <h2 className="mt-3 font-serif text-4xl font-semibold leading-tight text-[#1f2a22] sm:text-5xl">
                Wellness education that leads somewhere practical.
              </h2>
            </div>
            <div className="grid gap-5 sm:grid-cols-3">
              {[
                {
                  icon: BookOpen,
                  title: 'Guides',
                  text: 'Focused book bundles for gut health, longevity, macros, hormones, GLP-1 support, and more.',
                },
                {
                  icon: Search,
                  title: 'Articles',
                  text: 'Health and nutrition articles written for clarity, not confusion.',
                },
                {
                  icon: Smartphone,
                  title: 'Tools',
                  text: 'The NutriTrack app turns reading into daily tracking, planning, and decisions.',
                },
              ].map((item) => {
                const Icon = item.icon;
                return (
                  <article key={item.title} className="rounded-2xl border border-[#d8cbb8] bg-[#fbf7ef] p-6 shadow-sm">
                    <Icon className="h-6 w-6 text-[#6f785f]" />
                    <h3 className="mt-4 font-serif text-2xl font-semibold text-[#1f2a22]">{item.title}</h3>
                    <p className="mt-3 text-sm leading-6 text-[#626b5d]">{item.text}</p>
                  </article>
                );
              })}
            </div>
          </div>
        </section>

        <section id="free-plan" className="bg-[#1f2a22] px-4 py-16 text-[#efe4d2] sm:px-6">
          <div className="mx-auto grid max-w-7xl gap-8 lg:grid-cols-[1fr_0.85fr] lg:items-center">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.28em] text-[#c9b99d]">Free starter plan</p>
              <h2 className="mt-3 font-serif text-4xl font-semibold leading-tight sm:text-5xl">
                Turn followers into warm leads before asking for the sale.
              </h2>
              <p className="mt-5 max-w-2xl text-base leading-8 text-[#d8cbb8]">
                The free 3-day macro starter plan gives new readers a useful first win, then points them to the complete Macros Made Easy book, printable PDF, and IHJ app bundle.
              </p>
            </div>
            <div className="rounded-[2rem] border border-[#d8cbb8]/25 bg-[#fbf7ef] p-6 text-[#1f2a22] shadow-xl shadow-black/20">
              <img src="/meta/ads/macros-made-easy/recipe-app-square.png" alt="Macros Made Easy recipe app bonus" className="aspect-[1.25/1] w-full rounded-2xl object-cover" loading="lazy" />
              <div className="mt-5 flex flex-col gap-3 sm:flex-row">
                <Link to="/free-macro-starter-plan" className="inline-flex flex-1 items-center justify-center gap-2 rounded-full bg-[#536343] px-6 py-3 text-sm font-bold text-white hover:bg-[#435236]">
                  Get the free plan <ArrowRight className="h-4 w-4" />
                </Link>
                <Link to="/books/macros-made-easy-for-women" className="inline-flex flex-1 items-center justify-center gap-2 rounded-full border border-[#b9a98f] px-6 py-3 text-sm font-bold text-[#334033] hover:bg-[#efe4d2]">
                  View bundle
                </Link>
              </div>
            </div>
          </div>
        </section>

        <section id="books" className="bg-[#fbf7ef] px-4 py-20 sm:px-6">
          <div className="mx-auto max-w-7xl">
            <div className="flex flex-col gap-5 sm:flex-row sm:items-end sm:justify-between">
              <div>
                <p className="text-xs font-bold uppercase tracking-[0.28em] text-[#7b806f]">Books</p>
                <h2 className="mt-3 font-serif text-4xl font-semibold text-[#1f2a22]">Digital wellness bundles</h2>
                <p className="mt-3 max-w-2xl text-base leading-7 text-[#626b5d]">
                  Each health bundle includes a Kindle and Apple Books compatible edition, printable PDF, and free Integrative Health Journal app access where included.
                </p>
              </div>
              <Link to="/books" className="inline-flex items-center justify-center gap-2 rounded-full border border-[#b9a98f] px-5 py-3 text-sm font-bold text-[#334033] hover:bg-[#efe4d2]">
                View all books <ArrowRight className="h-4 w-4" />
              </Link>
            </div>

            <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
              {featuredBooks.map((book) => (
                <Link key={book.slug} to={book.slug === 'macros-made-easy-for-women' ? '/books/macros-made-easy-for-women' : `/books#${book.slug}`} className="group rounded-2xl border border-[#d8cbb8] bg-[#f8f3ea] p-4 shadow-sm transition hover:-translate-y-1 hover:shadow-xl hover:shadow-[#9b8d73]/15">
                  <img src={book.image} alt={`${book.title} cover`} className="aspect-[2/3] w-full rounded-xl object-cover shadow-md ring-1 ring-[#806f52]/15" loading="lazy" />
                  <div className="pt-5">
                    <p className="text-xs font-bold uppercase tracking-[0.18em] text-[#7b806f]">{book.category}</p>
                    <h3 className="mt-2 font-serif text-2xl font-semibold leading-tight text-[#1f2a22]">{book.title}</h3>
                    <p className="mt-2 line-clamp-2 text-sm leading-6 text-[#626b5d]">{book.subtitle}</p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        <section id="apps" className="px-4 py-20 sm:px-6">
          <div className="mx-auto grid max-w-7xl gap-12 lg:grid-cols-[0.95fr_1.05fr] lg:items-center">
            <div className="overflow-hidden rounded-[2rem] border border-[#d8cbb8] bg-[#fbf7ef] p-4 shadow-xl shadow-[#9b8d73]/15">
              <img
                src="/images/app-hero-screenshot.png"
                alt="NutriTrack app dashboard"
                className="w-full rounded-2xl object-cover"
                loading="lazy"
              />
            </div>
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.28em] text-[#7b806f]">NutriTrack App</p>
              <h2 className="mt-3 font-serif text-4xl font-semibold leading-tight text-[#1f2a22] sm:text-5xl">
                The IHJ companion app for nutrition follow-through.
              </h2>
              <p className="mt-5 text-base leading-8 text-[#626b5d]">
                NutriTrack is the practical app side of Integrative Health Journal. Use it to track food, calculate macros, ask the AI nutrition coach, build grocery lists, and connect daily choices back to your goals.
              </p>
              <div className="mt-8 grid gap-4">
                {APP_FEATURES.map((feature) => {
                  const Icon = feature.icon;
                  return (
                    <div key={feature.title} className="flex gap-4 rounded-2xl border border-[#d8cbb8] bg-[#fbf7ef] p-5">
                      <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-[#e4dcc8] text-[#536343]">
                        <Icon className="h-5 w-5" />
                      </div>
                      <div>
                        <h3 className="font-serif text-xl font-semibold text-[#1f2a22]">{feature.title}</h3>
                        <p className="mt-1 text-sm leading-6 text-[#626b5d]">{feature.text}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="mt-8 flex flex-col gap-3 sm:flex-row">
                <Link to={appCta} className="inline-flex items-center justify-center gap-2 rounded-full bg-[#536343] px-6 py-3 text-sm font-bold text-white shadow-md hover:bg-[#435236]">
                  {appCtaLabel} <ArrowRight className="h-4 w-4" />
                </Link>
                <Link to="/login?tab=book" className="inline-flex items-center justify-center gap-2 rounded-full border border-[#b9a98f] px-6 py-3 text-sm font-bold text-[#334033] hover:bg-[#efe4d2]">
                  I have a book code
                </Link>
              </div>
            </div>
          </div>
        </section>

        <section id="articles" className="bg-[#ede3d3] px-4 py-20 sm:px-6">
          <div className="mx-auto max-w-7xl">
            <div className="flex flex-col gap-5 sm:flex-row sm:items-end sm:justify-between">
              <div>
                <p className="text-xs font-bold uppercase tracking-[0.28em] text-[#7b806f]">Articles</p>
                <h2 className="mt-3 font-serif text-4xl font-semibold text-[#1f2a22]">From the journal</h2>
                <p className="mt-3 max-w-2xl text-base leading-7 text-[#626b5d]">
                  Clear nutrition and integrative health writing for readers who want useful decisions, not overwhelm.
                </p>
              </div>
              <Link to="/blog" className="inline-flex items-center justify-center gap-2 rounded-full border border-[#b9a98f] bg-[#fbf7ef] px-5 py-3 text-sm font-bold text-[#334033] hover:bg-[#f8f3ea]">
                Browse articles <ArrowRight className="h-4 w-4" />
              </Link>
            </div>

            <div className="mt-10 grid gap-5 md:grid-cols-3">
              {recentPosts.length > 0 ? recentPosts.map((post) => (
                <Link key={post.slug} to={`/${post.slug}`} className="group overflow-hidden rounded-2xl border border-[#d8cbb8] bg-[#fbf7ef] shadow-sm transition hover:-translate-y-1 hover:shadow-xl hover:shadow-[#9b8d73]/15">
                  <img
                    src={post.featured_image || `${API_URL}/blog/image/${post.slug}`}
                    alt={decodeEntities(post.title)}
                    className="h-48 w-full object-cover transition group-hover:scale-[1.03]"
                    loading="lazy"
                  />
                  <div className="p-6">
                    {post.category && (
                      <p className="text-xs font-bold uppercase tracking-[0.18em] text-[#7b806f]">{post.category}</p>
                    )}
                    <h3 className="mt-2 font-serif text-2xl font-semibold leading-tight text-[#1f2a22]">{decodeEntities(post.title)}</h3>
                    <div className="mt-4 flex items-center gap-2 text-xs text-[#7b806f]">
                      <Calendar className="h-4 w-4" />
                      {formatDate(post.published_at)}
                    </div>
                  </div>
                </Link>
              )) : (
                <div className="md:col-span-3 rounded-2xl border border-[#d8cbb8] bg-[#fbf7ef] p-8 text-center text-[#626b5d]">
                  Articles are loading. Visit the journal archive for the full library.
                </div>
              )}
            </div>
          </div>
        </section>

        <section className="px-4 py-20 sm:px-6">
          <div className="mx-auto max-w-5xl rounded-[2rem] border border-[#d8cbb8] bg-[#fbf7ef] p-8 text-center shadow-xl shadow-[#9b8d73]/10 sm:p-12">
            <img src="/images/ihj-facebook-logo.jpg" alt="" className="mx-auto h-20 w-20 rounded-full object-cover shadow-sm ring-1 ring-[#c9b99d]" />
            <h2 className="mt-5 font-serif text-4xl font-semibold text-[#1f2a22]">
              Build the routine, not just the reading list.
            </h2>
            <p className="mx-auto mt-4 max-w-2xl text-base leading-8 text-[#626b5d]">
              Start with the book that fits your goal, read the journal for context, and use NutriTrack to make the plan usable day to day.
            </p>
            <div className="mt-7 flex flex-col justify-center gap-3 sm:flex-row">
              <Link to="/books" className="inline-flex items-center justify-center gap-2 rounded-full bg-[#536343] px-6 py-3 text-sm font-bold text-white hover:bg-[#435236]">
                Shop books <ArrowRight className="h-4 w-4" />
              </Link>
              <Link to={appCta} className="inline-flex items-center justify-center gap-2 rounded-full border border-[#b9a98f] px-6 py-3 text-sm font-bold text-[#334033] hover:bg-[#efe4d2]">
                Open NutriTrack <Smartphone className="h-4 w-4" />
              </Link>
            </div>
          </div>
        </section>
      </main>

      <footer className="border-t border-[#d8cbb8] bg-[#1f2a22] px-4 py-10 text-[#efe4d2] sm:px-6">
        <div className="mx-auto flex max-w-7xl flex-col gap-6 md:flex-row md:items-center md:justify-between">
          <div className="flex items-center gap-3">
            <img src="/images/ihj-facebook-logo.jpg" alt="Integrative Health Journal" className="h-12 w-12 rounded-full object-cover shadow-sm ring-1 ring-[#d8cbb8]/40" />
            <div>
              <div className="font-serif text-xl font-semibold">Integrative Health Journal</div>
              <div className="text-xs uppercase tracking-[0.2em] text-[#c9b99d]">Books, articles, apps</div>
            </div>
          </div>
          <div className="flex flex-wrap gap-5 text-sm text-[#d8cbb8]">
            <Link to="/books" className="hover:text-white">Books</Link>
            <Link to="/blog" className="hover:text-white">Articles</Link>
            <Link to={appCta} className="hover:text-white">NutriTrack</Link>
            <Link to="/login?tab=book" className="hover:text-white">Book Code</Link>
          </div>
          <p className="text-xs text-[#b9a98f]">&copy; {new Date().getFullYear()} Integrative Health Journal.</p>
        </div>
      </footer>
    </div>
  );
}
